<template>
  <div class="pomodoro-navbar" v-if="shouldShow" @click.stop>
    <div 
      class="pomodoro-compact"
      :class="{
        'running': pomodoro.state.isRunning,
        'paused': pomodoro.state.isPaused,
        'work': pomodoro.state.mode === 'work',
        'break': pomodoro.state.mode !== 'work'
      }"
      @click.stop="toggleTimer"
    >
      <!-- Indicador de progreso circular -->
      <div class="progress-indicator">
        <svg width="32" height="32" class="progress-circle">
          <circle
            cx="16"
            cy="16"
            r="14"
            stroke="rgba(0,0,0,0.1)"
            stroke-width="2"
            fill="none"
          />
          <circle
            cx="16"
            cy="16"
            r="14"
            stroke="currentColor"
            stroke-width="2"
            fill="none"
            :stroke-dasharray="87.92"
            :stroke-dashoffset="87.92 - (pomodoro.progress / 100) * 87.92"
            stroke-linecap="round"
            transform="rotate(-90 16 16)"
            class="progress-bar"
          />
        </svg>
        
        <!-- Icono de estado -->
        <div class="status-icon">
          <i 
            :class="pomodoro.state.isRunning ? 'ri-pause-fill' : 'ri-play-fill'"
            v-if="pomodoro.state.time > 0"
          ></i>
          <i class="ri-check-fill" v-else></i>
        </div>
      </div>
      
      <!-- Información del tiempo -->
      <div class="time-info">
        <div class="time-display">{{ pomodoro.formattedTime }}</div>
        <div class="mode-display">{{ getModeLabel() }}</div>
      </div>
      
      <!-- Estadísticas rápidas -->
      <div class="quick-stats">
        <div class="sessions-today">
          <i class="ri-check-line"></i>
          <span>{{ pomodoro.stats.today.completed }}</span>
        </div>
      </div>
    </div>
    
    <!-- Botón de configuración rápida -->
    <div class="quick-actions" @click.stop>
      <button 
        class="action-btn reset-btn"
        @click.stop="resetTimer"
        :disabled="!pomodoro.canReset"
        title="Reiniciar"
      >
        <i class="ri-refresh-line"></i>
      </button>
      
      <div class="mode-switcher">
        <button
          class="mode-btn"
          :class="{ active: pomodoro.state.mode === 'work' }"
          @click.stop="switchToWork"
          title="Modo Trabajo"
        >
          <i class="ri-briefcase-line"></i>
        </button>
        <button
          class="mode-btn"
          :class="{ active: pomodoro.state.mode !== 'work' }"
          @click.stop="switchToBreak"
          title="Modo Descanso"
        >
          <i class="ri-cup-line"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { usePomodoro } from '@/composables/usePomodoro'

export default {
  name: 'PomodoroNavbar',
  setup() {
    const route = useRoute()
    const pomodoro = usePomodoro()
    const hasBeenActive = ref(false)

    // 1. Monitoreamos el estado del pomodoro para manejar de forma segura el flag de actividad (Side Effects)
    watch(
      () => [
        pomodoro.state.isRunning,
        pomodoro.state.isPaused,
        pomodoro.state.completedPomodoros,
        pomodoro.state.time,
        pomodoro.state.mode
      ],
      () => {
        const isCurrentlyActive = pomodoro.state.isRunning || 
                                 pomodoro.state.isPaused || 
                                 pomodoro.state.completedPomodoros > 0 ||
                                 pomodoro.state.time !== pomodoro.getTimeForMode(pomodoro.state.mode)
        
        if (isCurrentlyActive) {
          hasBeenActive.value = true
        }
        
        // Si se ha reiniciado completamente
        const isFullyReset = pomodoro.state.time === pomodoro.getTimeForMode(pomodoro.state.mode) && 
                             pomodoro.state.completedPomodoros === 0 && 
                             !pomodoro.state.isRunning && 
                             !pomodoro.state.isPaused

        if (isFullyReset) {
          hasBeenActive.value = false
        }
      },
      { immediate: true }
    )

    // 2. Ahora shouldShow es una propiedad computada PURA (sin efectos secundarios)
    const shouldShow = computed(() => {
      if (route.name === 'Welcome') return false
      return hasBeenActive.value
    })

    // Toggle timer
    const toggleTimer = () => {
      if (pomodoro.state.isRunning) {
        pomodoro.pauseTimer()
      } else if (pomodoro.state.time > 0) {
        pomodoro.startTimer()
      }
    }
    
    // Reset timer
    const resetTimer = () => {
      pomodoro.resetTimer()
    }
    
    // Switch to work mode
    const switchToWork = () => {
      pomodoro.switchMode('work')
    }
    
    // Switch to break mode
    const switchToBreak = () => {
      pomodoro.switchMode('shortBreak')
    }

    // Obtener etiqueta del modo de forma compacta
    const getModeLabel = () => {
      switch (pomodoro.state.mode) {
        case 'work':
          return 'Trabajo'
        case 'shortBreak':
          return 'Descanso'
        case 'longBreak':
          return 'Descanso L.'
        default:
          return 'Trabajo'
      }
    }

    return {
      pomodoro,
      shouldShow,
      toggleTimer,
      resetTimer,
      switchToWork,
      switchToBreak,
      getModeLabel
    }
  }
}
</script>

<style scoped>
.pomodoro-navbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  user-select: none;
  font-size: 0.75rem;
  position: relative;
  z-index: 10;
}

.pomodoro-compact {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  color: var(--bs-body-color);
}

.pomodoro-compact:hover {
  color: var(--bs-primary);
}

.pomodoro-compact.running {
  color: #4361ee;
}

.pomodoro-compact.paused {
  color: #f59e0b;
}

.pomodoro-compact.work {
  border-left: 3px solid #4361ee;
  padding-left: 0.5rem;
}

.pomodoro-compact.break {
  border-left: 3px solid #17ad37;
  padding-left: 0.5rem;
}

/* Indicador de progreso */
.progress-indicator {
  position: relative;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.progress-circle {
  width: 100%;
  height: 100%;
}

.progress-bar {
  transition: stroke-dashoffset 0.3s ease;
}

.status-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.7rem;
  color: currentColor;
}

/* Información del tiempo */
.time-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 60px;
}

.time-display {
  font-weight: 700;
  font-size: 0.85rem;
  line-height: 1;
  color: currentColor;
}

.mode-display {
  font-size: 0.65rem;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: currentColor;
}

/* Estadísticas rápidas */
.quick-stats {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.sessions-today {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
  opacity: 0.8;
  color: currentColor;
}

.sessions-today i {
  font-size: 0.65rem;
}

/* Acciones rápidas */
.quick-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: 0.25rem;
  padding-left: 0.25rem;
  border-left: 1px solid rgba(0, 0, 0, 0.1);
}

.action-btn {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
  color: var(--bs-text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 0.7rem;
}

.action-btn:hover {
  background: rgba(0, 0, 0, 0.1);
  color: var(--bs-body-color);
  transform: scale(1.05);
}

.action-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
  transform: none;
}

/* Selector de modo */
.mode-switcher {
  display: flex;
  gap: 2px;
}

.mode-btn {
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.05);
  color: var(--bs-text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 0.6rem;
}

.mode-btn:hover {
  background: rgba(0, 0, 0, 0.1);
  color: var(--bs-body-color);
}

.mode-btn.active {
  background: var(--bs-primary);
  color: white;
}

/* Animaciones */
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.pomodoro-compact.running .progress-bar {
  animation: pulse 2s infinite;
}

/* Responsive */
@media (max-width: 768px) {
  .pomodoro-navbar {
    padding: 0.25rem;
    gap: 0.25rem;
  }
  
  .time-info {
    min-width: 50px;
  }
  
  .time-display {
    font-size: 0.75rem;
  }
  
  .mode-display {
    font-size: 0.6rem;
  }
  
  .quick-stats {
    display: none;
  }
  
  .quick-actions {
    gap: 0.125rem;
  }
  
  .action-btn {
    width: 20px;
    height: 20px;
    font-size: 0.6rem;
  }
  
  .mode-btn {
    width: 18px;
    height: 18px;
    font-size: 0.55rem;
  }
}

@media (max-width: 480px) {
  .mode-switcher {
    display: none;
  }
  
  .quick-actions {
    border-left: none;
    padding-left: 0;
    margin-left: 0;
  }
}

/* Modo oscuro */
body.dark-mode-active .pomodoro-navbar {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .pomodoro-compact {
  color: rgba(255, 255, 255, 0.9);
}

body.dark-mode-active .pomodoro-compact:hover {
  color: white;
}

body.dark-mode-active .pomodoro-compact.running {
  color: #4fc3f7;
}

body.dark-mode-active .pomodoro-compact.paused {
  color: #fbbf24;
}

body.dark-mode-active .pomodoro-compact.work {
  border-left-color: #4fc3f7;
}

body.dark-mode-active .pomodoro-compact.break {
  border-left-color: #4fc3f7;
}

body.dark-mode-active .progress-circle circle:first-child {
  stroke: rgba(255, 255, 255, 0.2);
}

body.dark-mode-active .quick-actions {
  border-left-color: rgba(255, 255, 255, 0.2);
}

body.dark-mode-active .action-btn {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.8);
}

body.dark-mode-active .action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

body.dark-mode-active .mode-btn {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.6);
}

body.dark-mode-active .mode-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
}

body.dark-mode-active .mode-btn.active {
  background: #4fc3f7;
  color: white;
}
</style>